import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Save, Users, Settings, Plus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  bcBriefSegments, bcBriefStats, bcBriefEditions, bcBriefEditionCreate,
  bcBriefEditionSend, bcBriefSegmentPatch,
} from "@/lib/api";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const CADENCES = ["weekly", "biweekly", "monthly"];

export default function AdminBrief() {
  const [tab, setTab] = useState("editions");
  const [segments, setSegments] = useState([]);
  const [stats, setStats] = useState(null);
  const [editions, setEditions] = useState([]);
  const [error, setError] = useState("");

  const refresh = async () => {
    setError("");
    try {
      const [s, st, e] = await Promise.all([bcBriefSegments(), bcBriefStats(), bcBriefEditions()]);
      setSegments(s.items || []); setStats(st); setEditions(e.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load — are you signed in as admin/editor?");
    }
  };
  useEffect(() => { document.title = "AI Brief Studio | Admin"; refresh(); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="admin-brief">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/admin" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Admin</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Weekly AI Brief Studio</h1>
          <p className="mt-2 text-slate-600">Segment cadence, edition composition and audience health.</p>

          {stats && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total" value={stats.total} testId="admin-brief-stat-total" />
              <Stat label="Verified" value={stats.verified} tone="emerald" testId="admin-brief-stat-verified" />
              <Stat label="Pending" value={stats.pending} tone="amber" testId="admin-brief-stat-pending" />
              <Stat label="Unsubscribed" value={stats.unsubscribed} tone="slate" testId="admin-brief-stat-unsub" />
            </div>
          )}

          <div className="mt-6 border-b border-slate-200 flex gap-2 text-sm">
            <TabBtn active={tab === "editions"} onClick={() => setTab("editions")} testId="admin-brief-tab-editions">Editions</TabBtn>
            <TabBtn active={tab === "segments"} onClick={() => setTab("segments")} testId="admin-brief-tab-segments">Segments & cadence</TabBtn>
          </div>

          {error && <div className="mt-4 text-sm text-rose-700">{error}</div>}

          {tab === "editions" && (
            <EditionsTab segments={segments} editions={editions} onRefresh={refresh} />
          )}
          {tab === "segments" && (
            <SegmentsTab segments={segments} stats={stats} onRefresh={refresh} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function EditionsTab({ segments, editions, onRefresh }) {
  const [form, setForm] = useState({
    segment: "general", subject: "", intro: "", development: "",
    product_pick: "", automation_pick: "", action_item: "", body_html: "",
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const create = async () => {
    setBusy(true); setNotice("");
    try {
      await bcBriefEditionCreate(form);
      setForm({ ...form, subject: "", intro: "", development: "", product_pick: "", automation_pick: "", action_item: "", body_html: "" });
      setNotice("Edition drafted.");
      onRefresh();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Create failed.");
    } finally { setBusy(false); }
  };
  const send = async (id) => {
    if (!window.confirm("Send this edition to all verified subscribers of the segment?")) return;
    setBusy(true); setNotice("");
    try {
      const r = await bcBriefEditionSend(id);
      setNotice(`Sent to ${r.recipients_count} subscribers.`);
      onRefresh();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Send failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Composer */}
      <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="admin-brief-composer">
        <div className="flex items-center gap-2 text-sm font-bold text-indigo-700 uppercase tracking-widest">
          <Plus className="w-4 h-4" /> Compose edition
        </div>
        <div className="mt-4 space-y-3">
          <FormRow label="Segment">
            <select value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })}
              data-testid="admin-brief-segment"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              {segments.map((s) => <option key={s.key} value={s.key}>{s.label} ({s.cadence})</option>)}
            </select>
          </FormRow>
          <FormRow label="Subject">
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="admin-brief-subject" required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Intro">
            <textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })}
              data-testid="admin-brief-intro" rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Development of the week">
            <textarea value={form.development} onChange={(e) => setForm({ ...form, development: e.target.value })}
              data-testid="admin-brief-dev" rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Product pick">
            <textarea value={form.product_pick} onChange={(e) => setForm({ ...form, product_pick: e.target.value })}
              data-testid="admin-brief-prod" rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Automation pick">
            <textarea value={form.automation_pick} onChange={(e) => setForm({ ...form, automation_pick: e.target.value })}
              data-testid="admin-brief-auto" rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Action item">
            <textarea value={form.action_item} onChange={(e) => setForm({ ...form, action_item: e.target.value })}
              data-testid="admin-brief-action" rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Custom HTML body (optional — overrides sections above)">
            <textarea value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              data-testid="admin-brief-html" rows={4}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
          </FormRow>
        </div>
        <div className="mt-4">
          <button onClick={create} disabled={busy || !form.subject}
            data-testid="admin-brief-save"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 px-5 py-2.5 text-white font-semibold text-sm transition">
            <Save className="w-4 h-4" /> Save draft
          </button>
        </div>
      </section>

      {/* Editions list */}
      <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="admin-brief-list">
        <div className="text-sm font-bold uppercase tracking-widest text-slate-700">Editions</div>
        <div className="mt-3 space-y-2 max-h-[600px] overflow-y-auto">
          {editions.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 p-3" data-testid={`admin-brief-edition-${e.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{e.segment} · {e.status}</div>
                  <div className="text-sm font-bold text-slate-900">{e.subject}</div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    {e.status === "sent" ? `Sent to ${e.recipients_count} on ${new Date(e.sent_at).toLocaleString()}` : `Drafted ${new Date(e.created_at).toLocaleString()}`}
                  </div>
                </div>
                {e.status !== "sent" && (
                  <button onClick={() => (async () => {})()} className="hidden" />
                )}
                {e.status === "draft" && (
                  <button onClick={() => {
                    if (!window.confirm("Send this edition to all verified subscribers of the segment?")) return;
                    bcBriefEditionSend(e.id).then(() => window.location.reload()).catch(() => alert("Send failed"));
                  }} data-testid={`admin-brief-send-${e.id}`}
                    className="text-xs inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded px-3 py-1.5 transition">
                    <Send className="w-3 h-3" /> Send now
                  </button>
                )}
              </div>
            </div>
          ))}
          {editions.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No editions yet.</div>}
        </div>
      </section>
    </div>
  );
}

function SegmentsTab({ segments, stats, onRefresh }) {
  const [busy, setBusy] = useState("");
  const patch = async (key, patchObj) => {
    setBusy(key);
    try {
      await bcBriefSegmentPatch(key, patchObj);
      onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };
  const bySeg = Object.fromEntries((stats?.by_segment || []).map((r) => [r.key, r.count]));
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {segments.map((s) => (
        <div key={s.key} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-brief-seg-${s.key}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.key}</div>
              <div className="text-lg font-bold text-slate-900">{s.label}</div>
            </div>
            <span className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <Users className="w-3 h-3" /> {bySeg[s.key] ?? 0}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <FormRow label="Cadence">
              <select defaultValue={s.cadence}
                onChange={(e) => patch(s.key, { cadence: e.target.value })}
                data-testid={`admin-brief-seg-cadence-${s.key}`}
                disabled={busy === s.key}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormRow>
            <FormRow label="Send day">
              <select defaultValue={s.send_day}
                onChange={(e) => patch(s.key, { send_day: e.target.value })}
                data-testid={`admin-brief-seg-day-${s.key}`}
                disabled={busy === s.key}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FormRow>
          </div>
          <FormRow label="Description">
            <input defaultValue={s.description}
              onBlur={(e) => e.target.value !== s.description && patch(s.key, { description: e.target.value })}
              data-testid={`admin-brief-seg-desc-${s.key}`}
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </FormRow>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, tone = "indigo", testId }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
                emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
                amber: "bg-amber-50 text-amber-800 border-amber-200",
                slate: "bg-slate-100 text-slate-700 border-slate-200" }[tone];
  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest font-bold opacity-75">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testId }) {
  return <button onClick={onClick} data-testid={testId}
    className={`px-3 py-2 border-b-2 font-semibold ${active ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{children}</button>;
}
function FormRow({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
